import { FlexBox, Icon, Spinner, Text } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { HTMLAttributes, useRef } from 'react';

import { AppLink } from 'components/appLink';

import { AnalyticsSidenavItem as AnalyticsSidenavItemT } from '../../analyticsSidenav.types';

import styles from './analyticsSidenavItem.module.scss';

type AnalyticsSidenavItemProps = {
  item: AnalyticsSidenavItemT;
};

export const AnalyticsSidenavItem = ({
  item,
}: AnalyticsSidenavItemProps): JSX.Element => {
  const subItemsRef = useRef<HTMLDivElement>(null);

  const renderItem = (): JSX.Element => {
    const baseProps: HTMLAttributes<HTMLElement> = {
      className: classNames(styles.item, {
        [styles.itemActive]: item.active,
        [styles.itemAccordion]: item.type === 'accordion',
        [styles.itemLink]: item.type === 'link',
        [styles.itemButton]: item.type === 'button',
      }),
      role: 'menuitem',
    };

    const iconAndTitle = (
      <>
        {item.icon && (
          <FlexBox role="img" aria-label={item.title}>
            {item.type === 'accordion' && item.options.isLoading ? (
              <Spinner size={20} />
            ) : (
              <Icon
                className={styles.icon}
                name={item.icon}
                color="secondary"
                size={20}
              />
            )}
          </FlexBox>
        )}

        <Text.Paragraph className={styles.title} variant="1" as="span">
          {item.title}
        </Text.Paragraph>
      </>
    );

    switch (item.type) {
      default:
      case 'header':
        return (
          <div {...baseProps}>
            {iconAndTitle} {item.options.action}
          </div>
        );

      case 'accordion':
        return (
          <button {...baseProps} onClick={() => item.options.onToggle?.()}>
            {iconAndTitle}

            <FlexBox as="div" gap="200" alignItems="center">
              {item.options.action}
              <Icon
                name={item.options.isExpanded ? 'angle-down' : 'angle-right'}
              />
            </FlexBox>
          </button>
        );

      case 'link':
        return (
          <AppLink {...baseProps} {...item.options}>
            {iconAndTitle}
          </AppLink>
        );

      case 'button':
        return (
          <button {...baseProps} {...item.options}>
            {iconAndTitle}
          </button>
        );
    }
  };

  const renderSubItems = (): JSX.Element | null => {
    if (item.type !== 'header' && item.type !== 'accordion') {
      return null;
    }

    const subItemsElement = (
      <div ref={subItemsRef} className={styles.subItems}>
        {item.options.subItems.map((subItem) => {
          if (subItem.disabled) {
            return (
              <Text.Paragraph
                key={subItem.title}
                className={styles.subItem}
                variant="2"
                color="tertiary"
                as="span"
              >
                {subItem.title}
              </Text.Paragraph>
            );
          }

          return (
            <AppLink
              key={subItem.title}
              className={classNames(styles.subItem, styles.subItemLink, {
                [styles.subItemActive]: subItem.active,
              })}
              {...(!item.options.isExpanded && { tabIndex: -1 })}
              {...subItem.options}
            >
              <Text.Paragraph as="span" {...subItem.textProps}>
                {subItem.title}
              </Text.Paragraph>
            </AppLink>
          );
        })}
      </div>
    );

    if (item.type === 'accordion') {
      return (
        <div
          className={styles.accordion}
          style={{
            height: item.options.isExpanded
              ? (subItemsRef?.current?.scrollHeight ?? 0) + 8
              : 0,
          }}
        >
          {subItemsElement}
        </div>
      );
    }

    return subItemsElement;
  };

  return (
    <>
      {renderItem()}
      {renderSubItems()}
    </>
  );
};
